/**
 * LMSChairDashboardPage.jsx — Chair Readiness Console
 */
import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Trophy, Users, ScrollText, Award } from "lucide-react";
import { Hero, Kpi, Loading, EmptyState } from "./LMSTrainerDashboardPage";

export default function LMSChairDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/lms/chair/dashboard");
        setData(r.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <DashboardLayout title="Chair Readiness">
      <div data-testid="lmsx-chair-dashboard" className="space-y-6 pb-12">
        <Hero
          eyebrow="Chair · Diplomexa Academy"
          title="Your committee's readiness, at a glance"
          subtitle="Track delegate readiness, paper submissions, and identify who needs extra prep before placards rise."
        />
        {loading || !data ? <Loading /> : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Kpi icon={Users} label="Delegates" value={data.summary?.total_delegates ?? 0} testid="lmsx-chair-delegates" />
              <Kpi icon={Trophy} label="Avg readiness" value={`${data.summary?.avg_overall ?? 0}`} testid="lmsx-chair-avg-readiness" />
              <Kpi icon={ScrollText} label="Events covered" value={(data.events || []).length} testid="lmsx-chair-events" />
              <Kpi icon={Award} label="Committee" value="Live" testid="lmsx-chair-committee" />
            </div>

            <section
              data-testid="lmsx-chair-readiness-table"
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
                <Trophy size={14} className="text-[#D4AF37]" /> Delegate readiness leaderboard
              </h2>
              <div className="mt-3 space-y-2">
                {(data.delegate_readiness || []).length === 0 ? (
                  <EmptyState label="No delegates assigned to your committee yet." />
                ) : (
                  data.delegate_readiness.map((d, i) => (
                    <div key={d.user_id || i}
                          data-testid={`lmsx-chair-delegate-${i}`}
                          className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
                      <div className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-[#D4AF37] text-xs font-bold">
                        #{i + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-[#0A192F] truncate">{d.name || d.user_id}</p>
                        <p className="mt-0.5 text-[11px] text-slate-500">
                          {d.country || "—"} · {d.committee || "Committee"} · {d.position_papers_submitted || 0} paper(s) submitted
                        </p>
                      </div>
                      <div className="w-40 text-right">
                        <p className="text-xs font-semibold text-[#0F4C81]">{d.tier}</p>
                        <div className="mt-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-[#0F4C81] to-[#D4AF37]"
                                style={{ width: `${d.overall_score || 0}%` }} />
                        </div>
                        <p className="mt-0.5 text-[10px] text-slate-500">{d.overall_score}/100</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
